import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronDown, ChevronUp, User, Calendar, Users } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import NotificationBell from '../components/NotificationBell';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * Moment-First Home
 * 
 * Philosophy: Help friends decide, participate, and show up IRL.
 * Not designed to maximize scrolling or clicks.
 * 
 * Structure:
 * 1. Moment Header - "Tonight" / "This Weekend" / "Near You"
 * 2. Single Primary Card - ONE highlighted activity, large and calm
 * 3. Light Social Context - Friends attending/hosting (counts only)
 * 4. Secondary Exploration - Collapsed, optional
 */

export default function Home() {
  const navigate = useNavigate();
  const { user, locationData } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [happeningData, setHappeningData] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!locationData) {
      navigate('/');
      return;
    }
    fetchHappening();
  }, [locationData, navigate]);

  const fetchHappening = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [happeningRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/happening`, { headers }),
        axios.get(`${API}/api/categories`)
      ]);
      setHappeningData(happeningRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityClick = useCallback((item) => {
    if (item._type === 'event') {
      navigate(`/event/${item.event_id}`);
    } else if (item.post_id) {
      navigate(`/post/${item.post_id}`);
    } else if (item.server_id) {
      navigate(`/server/${item.server_id}`);
    }
  }, [navigate]);

  // Skeleton loader - calm, minimal
  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <Header user={user} locationData={locationData} navigate={navigate} />
        <main className="max-w-lg mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 rounded w-32" style={{ backgroundColor: 'var(--fy-surface-muted)' }} />
            <div className="h-64 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }} />
            <div className="h-12 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }} />
          </div>
        </main>
      </div>
    );
  }

  const moment = happeningData?.moment || { key: 'anytime', label: 'Near You' };
  const primary = happeningData?.primary;
  const secondary = happeningData?.secondary || [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      <Header user={user} locationData={locationData} navigate={navigate} />
      
      <main className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        
        {/* MOMENT HEADER - Frames time and relevance */}
        <MomentHeader moment={moment} locationData={locationData} />

        {/* PRIMARY ACTION CARD - One highlighted activity */}
        {primary ? (
          <PrimaryCard 
            item={primary} 
            onClick={() => handleActivityClick(primary)}
            categories={categories}
          />
        ) : (
          <EmptyState navigate={navigate} user={user} />
        )}

        {/* SECONDARY EXPLORATION - Collapsed, optional */}
        {secondary.length > 0 && (
          <SecondarySection 
            items={secondary}
            showMore={showMore}
            setShowMore={setShowMore}
            onItemClick={handleActivityClick}
            categories={categories}
          />
        )}

        {/* BROWSE CATEGORIES - Minimized */}
        <BrowseCategories 
          categories={categories} 
          navigate={navigate} 
        />

        {/* Subtle sign-in prompt for logged-out users */}
        {!user && (
          <div className="mt-8 text-center">
            <p className="text-sm mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              Sign in to see what friends are up to
            </p>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="text-sm"
              style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
            >
              Sign In
            </Button>
          </div>
        )}

      </main>
      
      {/* Footer with FAQ and Survey links for individuals */}
      <Footer />
    </div>
  );
}

// ============ COMPONENTS ============

function Header({ user, locationData, navigate }) {
  return (
    <nav className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
      <div className="max-w-lg mx-auto px-4">
        <div className="flex justify-between items-center h-14">
          <div className="flex items-center gap-3">
            <Sidebar />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
                <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
              </div>
              <span className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <NotificationBell />
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                size="sm"
                className="text-sm rounded-full px-4"
                style={{ backgroundColor: 'var(--fy-accent-positive)', color: 'var(--fy-background-primary)' }}
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function MomentHeader({ moment, locationData }) {
  return (
    <div className="mb-6">
      {/* Time-based moment label */}
      <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>
        {moment.label}
      </h1>
      
      {/* Location context - subtle */}
      {locationData && (
        <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
          <MapPin className="w-3.5 h-3.5" />
          <span>{locationData.town}, {locationData.state}</span>
        </div>
      )}
    </div>
  );
}

function PrimaryCard({ item, onClick, categories }) {
  const isEvent = item._type === 'event';
  const socialContext = item._social_context;
  
  // Get category info
  const categoryId = item.category || item.server_category;
  const category = categories.find(c => c.id === categoryId);
  const categoryIcon = category?.icon || '📌';

  return (
    <div 
      onClick={onClick}
      className="rounded-2xl p-5 sm:p-6 mb-4 cursor-pointer transition-all active:scale-[0.99]"
      style={{ 
        backgroundColor: 'var(--fy-surface-muted)', 
        border: '1px solid var(--fy-border-subtle)'
      }}
    >
      {/* Category & Type indicator */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">{categoryIcon}</span>
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--fy-text-secondary)' }}>
          {isEvent ? 'Event' : 'Activity'}
        </span>
      </div>

      {/* Title - Large, clear */}
      <h2 className="text-xl sm:text-2xl font-bold mb-2 leading-tight" style={{ color: 'var(--fy-text-primary)' }}>
        {isEvent ? item.title : (item.title || item.name)}
      </h2>

      {/* Description - Brief */}
      <p className="text-sm sm:text-base mb-4 line-clamp-2" style={{ color: 'var(--fy-text-secondary)' }}>
        {item.description}
      </p>

      {/* Meta info row */}
      <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
        {/* Time */}
        {isEvent && item.event_date && (
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            <span>{formatEventTime(item.event_date)}</span>
          </div>
        )}
        
        {/* Location */}
        {item.location && (
          <div className="flex items-center gap-1.5">
            <MapPin className="w-4 h-4" />
            <span>{item.location.town || item.location.state || 'Near you'}</span>
          </div>
        )}
      </div>

      {/* SOCIAL CONTEXT - Light, friend-based */}
      {socialContext && (
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
          <SocialContextBadge context={socialContext} />
        </div>
      )}

      {/* Primary action hint */}
      <div className="mt-5">
        <span className="inline-flex items-center font-medium text-sm" style={{ color: 'var(--fy-accent-positive)' }}>
          View details →
        </span>
      </div>
    </div>
  );
}

function SocialContextBadge({ context }) {
  if (!context) return null;
  
  const { friends_count, friend_hosting } = context;
  
  if (friend_hosting) {
    return (
      <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full w-fit" style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-accent-positive)' }}>
        <User className="w-4 h-4" />
        <span>A friend is hosting</span>
      </div>
    );
  }
  
  if (friends_count > 0) {
    return (
      <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full w-fit" style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-accent-positive)' }}>
        <Users className="w-4 h-4" />
        <span>
          {friends_count} {friends_count === 1 ? 'friend' : 'friends'} attending
        </span>
      </div>
    );
  }
  
  return null;
}

function EmptyState({ navigate, user }) {
  return (
    <div className="rounded-2xl p-8 text-center mb-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
      <div className="text-4xl mb-4">🌱</div>
      <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
        Nothing happening right now
      </h2>
      <p className="text-sm mb-6" style={{ color: 'var(--fy-text-secondary)' }}>
        Be the first to start something in your area.
      </p>
      <Button
        onClick={() => user ? navigate('/create-server') : navigate('/auth')}
        className="rounded-full px-6"
        style={{ backgroundColor: 'var(--fy-accent-positive)', color: 'var(--fy-background-primary)' }}
      >
        Create an activity
      </Button>
    </div>
  );
}

function SecondarySection({ items, showMore, setShowMore, onItemClick, categories }) {
  const visibleItems = showMore ? items : items.slice(0, 3);
  
  return (
    <div className="mb-6">
      {/* Toggle header */}
      <button
        onClick={() => setShowMore(!showMore)}
        className="w-full flex items-center justify-between py-3 text-sm font-medium transition-colors"
        style={{ color: 'var(--fy-text-secondary)' }}
      >
        <span>See more nearby ({items.length})</span>
        {showMore ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Collapsed list */}
      {showMore && (
        <div className="space-y-2 animate-fadeIn">
          {visibleItems.map((item, idx) => (
            <SecondaryItem 
              key={item.event_id || item.post_id || idx} 
              item={item}
              onClick={() => onItemClick(item)}
              categories={categories}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SecondaryItem({ item, onClick, categories }) {
  const isEvent = item._type === 'event';
  const categoryId = item.category || item.server_category;
  const category = categories.find(c => c.id === categoryId);
  const categoryIcon = category?.icon || '📌';
  
  return (
    <div 
      onClick={onClick}
      className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all"
      style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
    >
      <span className="text-xl">{categoryIcon}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate" style={{ color: 'var(--fy-text-primary)' }}>
          {isEvent ? item.title : (item.title || item.name)}
        </p>
        {isEvent && item.event_date && (
          <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>{formatEventTime(item.event_date)}</p>
        )}
      </div>
      {item._social_context && (
        <div className="flex-shrink-0">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--fy-accent-positive)' }} title="Friends connected" />
        </div>
      )}
    </div>
  );
}

function BrowseCategories({ categories, navigate }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="mt-6 pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-2 text-sm font-medium transition-colors"
        style={{ color: 'var(--fy-text-secondary)' }}
      >
        <span>Browse categories</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="grid grid-cols-2 gap-2 mt-3 animate-fadeIn">
          {categories.slice(0, 8).map((cat) => (
            <button
              key={cat.id}
              onClick={() => navigate(`/category/${cat.id}`)}
              className="flex items-center gap-2 p-3 rounded-xl text-left transition-all"
              style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
            >
              <span className="text-lg">{cat.icon}</span>
              <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{cat.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ HELPERS ============

function formatEventTime(dateStr) {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    if (diffHours < 0) return 'Past';
    if (diffHours < 1) return 'Starting soon';
    if (diffHours < 24) {
      const hours = Math.floor(diffHours);
      return `In ${hours} hour${hours === 1 ? '' : 's'}`;
    }
    if (diffHours < 48) return 'Tomorrow';
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return '';
  }
}
